import { z } from "zod";
import { CountryCode } from "../common/country.js";
import { CurrencyCode } from "../common/money.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
/**
 * The shop's bank account for the interim bank transfer (ADR 0005, PAY-10, card 055): buyers pay into it directly and
 * the platform never holds the money. Stored encrypted; each order keeps a copy of the details as they were when the
 * quote was accepted, so a change applies to new orders only.
 */
const Swift = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/)
    .describe("The bank's SWIFT or BIC code, 8 or 11 characters");
const Iban = z
    .string()
    .trim()
    .toUpperCase()
    .transform((value) => value.replaceAll(" ", ""))
    .pipe(z.string().regex(/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/))
    .describe("International Bank Account Number, where the country uses one");
/** Codes some countries route transfers by: sort code (GB), routing number (US), BSB (AU), bank and branch codes. */
export const LocalBankCodes = z
    .object({
    bankCode: z.string().trim().min(1).max(20).optional(),
    branchCode: z.string().trim().min(1).max(20).optional(),
    sortCode: z
        .string()
        .trim()
        .regex(/^\d{2}-?\d{2}-?\d{2}$/)
        .optional(),
    routingNumber: z
        .string()
        .trim()
        .regex(/^\d{9}$/)
        .optional(),
    bsb: z
        .string()
        .trim()
        .regex(/^\d{3}-?\d{3}$/)
        .optional(),
})
    .meta({ id: "LocalBankCodes" });
/** The details a buyer pays to. */
export const BankDetails = z
    .object({
    accountName: z.string().trim().min(2).max(140),
    bankName: z.string().trim().min(2).max(140),
    bankCountry: CountryCode,
    swiftBic: Swift,
    currency: CurrencyCode.describe("The currency the account takes"),
    accountNumber: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9 -]{4,34}$/)
        .optional(),
    iban: Iban.optional(),
    localCodes: LocalBankCodes.default({}),
})
    .refine((details) => details.accountNumber !== undefined || details.iban !== undefined, {
    path: ["accountNumber"],
    message: "REQUIRED",
})
    .meta({ id: "BankDetails" });
export const BankAccountView = z
    .object({
    accountName: z.string(),
    bankName: z.string(),
    bankCountry: CountryCode,
    swiftBic: z.string(),
    currency: CurrencyCode,
    accountEnding: z.string().describe("The last four characters of the account number or IBAN"),
    localCodes: LocalBankCodes,
    version: z.number().int().positive().describe("Sent as `If-Match` to change it"),
    updatedAt: Timestamp,
    legalName: z
        .string()
        .describe("The enterprise's registered legal name, which buyers see beside the account name"),
    matchesLegalName: z
        .boolean()
        .describe("Whether the account name is the registered legal name, read without case, punctuation, or the form of the company (card 103). When it is not, buyers are warned before they transfer"),
})
    .meta({ id: "BankAccount" });
const Shop = { surface: "tenant", auth: "user", permission: "shop:manage" };
export const getBankAccount = defineRoute({
    ...Shop,
    id: "catalog.shop.bank_account.get",
    method: "GET",
    path: "/v1/shop/bank-account",
    marks: ["tenant"],
    summary: "The shop's bank account for transfers, with the account number masked; `404` before one is set",
    prd: ["PAY-10"],
    responses: { 200: { description: "The account", schema: BankAccountView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const putBankAccount = defineRoute({
    ...Shop,
    id: "catalog.shop.bank_account.put",
    method: "PUT",
    path: "/v1/shop/bank-account",
    marks: ["tenant", "step-up", "if-match"],
    summary: "Set or replace the shop's bank account (`If-Match`: its version, `0` for the first). Every admin of the enterprise is told; orders already placed keep the details they were given",
    prd: ["PAY-10"],
    request: { body: BankDetails },
    responses: { 200: { description: "The account", schema: BankAccountView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "STEP_UP_REQUIRED",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
        "VERSION_CONFLICT",
    ],
});
export const bankAccountRoutes = [getBankAccount, putBankAccount];
//# sourceMappingURL=bank-account.routes.js.map